/**
 * Measurements, degrees, fractions, currency and phone numbers.
 *
 * Beam's abbreviations guide is explicit that the unit closes up against the
 * value: `50km`, `10cm`, `100mg`, `7.5kg`. Degrees take the symbol rather than
 * the word. Fractions are written with a slash, and ranges with a hyphen.
 */
import { VULGAR } from './numbers.js';
/**
 * Units that close up against the number.
 *
 * Bare `m` is included but guarded: it only matches when the following
 * character is not a letter, so `5 mg` cannot be read as five metres, and the
 * longest-alternative ordering below puts multi-letter units first.
 */
const UNITS = [
    'mmHg', 'mmol/L', 'mmol', 'mcg', 'mg', 'kg', 'mm', 'cm', 'km', 'ml', 'mL',
    'kPa', 'kJ', 'kcal', 'g', 'L', 'm', 'IU', 'units', 'unit',
];
const CANONICAL_UNIT = {
    ml: 'mL',
    l: 'L',
    mmhg: 'mmHg',
    kpa: 'kPa',
    kj: 'kJ',
    iu: 'IU',
};
const UNIT_ALT = UNITS
    .slice()
    .sort((a, b) => b.length - a.length)
    .map((u) => u.replace(/[/]/g, '\\/'))
    .join('|');
/** `10 cm`, `7.5 kg`, `100 mg`. The space is what we are removing. */
const SPACED_UNIT = new RegExp(String.raw `(?<![\w.])(\d+(?:\.\d+)?)\s+(${UNIT_ALT})\b(?![\w/])`, 'g');
/** `10cm` already closed up, but with a unit that needs its canonical casing. */
const TIGHT_UNIT = new RegExp(String.raw `(?<![\w.])(\d+(?:\.\d+)?)(${UNIT_ALT})\b(?![\w/])`, 'g');
/** `50 degrees`, `50 deg`, `50 degrees of flexion`. */
const DEGREES = /(?<![\w.])(\d+(?:\.\d+)?)\s*(?:degrees|degree|deg\.?)\b(?!\s*(?:c\b|celsius|centigrade|f\b|fahrenheit))/gi;
/** Temperature keeps the unit letter: `37.2 degrees Celsius` becomes `37.2°C`. */
const DEGREES_C = /(?<![\w.])(\d+(?:\.\d+)?)\s*(?:degrees?|deg\.?)\s*(?:c\b|celsius|centigrade)/gi;
/** `15 per cent`, `15 percent`, `15 %`. */
const PERCENT = /(?<![\w.])(\d+(?:\.\d+)?)\s*(?:per\s*cent|percent|%)/gi;
/** `seven and a half years`, `5 and a half`. Beam's guide writes these as 7 and a vulgar half. */
const AND_A_HALF = /\b(\d+|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve)\s+and\s+a\s+(half|quarter|third)\b/gi;
/** A written fraction that should use the vulgar glyph: `1/2`, `3/4`. */
const SIMPLE_FRACTION = /(?<![\w./])(1\/2|1\/4|3\/4|1\/3|2\/3)(?![\w./])/g;
/** `two thousand three hundred and fifty dollars`, `2350 dollars`, `$2350`. */
const DOLLARS_WORD = /(?<![\w.])(\d{1,3}(?:,\d{3})*(?:\.\d{1,2})?|\d+(?:\.\d{1,2})?)\s+dollars\b/gi;
const BARE_AMOUNT = /\$\s?(\d+(?:\.\d{1,2})?)\b/g;
/** Australian phone numbers. */
const PHONE_LANDLINE = /(?<![\d+])(?:0)?([2378])\s?(\d{4})\s?(\d{4})(?![\d])/g;
const PHONE_MOBILE = /(?<![\d+])(04\d{2})\s?(\d{3})\s?(\d{3})(?![\d])/g;
const PHONE_INTL = /\+61\s?\(?0?\)?\s?([2-478])\s?(\d{4})\s?(\d{4})(?![\d])/g;
const NUMBER_WORD_VALUE = {
    one: '1', two: '2', three: '3', four: '4', five: '5', six: '6',
    seven: '7', eight: '8', nine: '9', ten: '10', eleven: '11', twelve: '12',
};
/**
 * Units spoken or typed in full. Beam's guide abbreviates them against the
 * value, so `3 centimetres` becomes `3cm`. Dictation produces these constantly,
 * because a doctor says "three centimetres", not "three see em".
 */
const SPELLED_UNITS = {
    millimetre: 'mm', millimetres: 'mm', millimeter: 'mm', millimeters: 'mm',
    centimetre: 'cm', centimetres: 'cm', centimeter: 'cm', centimeters: 'cm',
    metre: 'm', metres: 'm',
    kilometre: 'km', kilometres: 'km', kilometer: 'km', kilometers: 'km',
    microgram: 'mcg', micrograms: 'mcg',
    milligram: 'mg', milligrams: 'mg',
    gram: 'g', grams: 'g',
    kilogram: 'kg', kilograms: 'kg', kilo: 'kg', kilos: 'kg',
    millilitre: 'mL', millilitres: 'mL', milliliter: 'mL', milliliters: 'mL',
    litre: 'L', litres: 'L', liter: 'L', liters: 'L',
};
const SPELLED_UNIT = new RegExp(String.raw `(?<![\w.])(\d+(?:\.\d+)?)\s+(` +
    Object.keys(SPELLED_UNITS).sort((a, b) => b.length - a.length).join('|') +
    String.raw `)\b`, 'gi');
function canonicalUnit(raw) {
    return CANONICAL_UNIT[raw.toLowerCase()] ?? raw;
}
function groupThousands(digits) {
    return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}
export function findMeasures(text, tightenUnits) {
    const hits = [];
    const claimed = [];
    const free = (s, e) => !claimed.some(([cs, ce]) => s < ce && cs < e);
    const push = (s, e, replacement, kind, confidence = 'certain', note) => {
        if (!free(s, e))
            return;
        claimed.push([s, e]);
        hits.push({ start: s, end: e, replacement, kind, confidence, ...(note ? { note } : {}) });
    };
    // Fractions first: "seven and a half" spans words a later pass would claim.
    for (const m of text.matchAll(AND_A_HALF)) {
        const rawWhole = m[1].toLowerCase();
        const whole = /^\d+$/.test(rawWhole) ? rawWhole : NUMBER_WORD_VALUE[rawWhole];
        if (!whole)
            continue;
        const glyph = { half: '½', quarter: '¼', third: '⅓' }[m[2].toLowerCase()];
        if (!glyph)
            continue;
        push(m.index, m.index + m[0].length, `${whole}${glyph}`, 'fraction', 'certain', "Beam's abbreviations guide writes these with a vulgar fraction");
    }
    for (const m of text.matchAll(SIMPLE_FRACTION)) {
        const glyph = VULGAR[m[1]];
        if (!glyph)
            continue;
        push(m.index, m.index + m[0].length, glyph, 'fraction');
    }
    for (const m of text.matchAll(DEGREES_C)) {
        push(m.index, m.index + m[0].length, `${m[1]}°C`, 'measure');
    }
    for (const m of text.matchAll(DEGREES)) {
        push(m.index, m.index + m[0].length, `${m[1]}°`, 'measure', 'certain', "the guide uses the degree symbol, as in 50°");
    }
    for (const m of text.matchAll(PERCENT)) {
        push(m.index, m.index + m[0].length, `${m[1]}%`, 'measure');
    }
    for (const m of text.matchAll(PHONE_INTL)) {
        push(m.index, m.index + m[0].length, `+61 ${m[1]} ${m[2]} ${m[3]}`, 'phone');
    }
    for (const m of text.matchAll(PHONE_MOBILE)) {
        push(m.index, m.index + m[0].length, `${m[1]} ${m[2]} ${m[3]}`, 'phone');
    }
    for (const m of text.matchAll(PHONE_LANDLINE)) {
        push(m.index, m.index + m[0].length, `(0${m[1]}) ${m[2]} ${m[3]}`, 'phone', 'likely', 'read as an Australian landline. Check it is not part of a longer reference number.');
    }
    for (const m of text.matchAll(DOLLARS_WORD)) {
        const digits = m[1].replace(/,/g, '');
        const [whole, cents] = digits.split('.');
        const formatted = `$${groupThousands(whole)}${cents ? `.${cents.padEnd(2, '0')}` : ''}`;
        push(m.index, m.index + m[0].length, formatted, 'currency');
    }
    for (const m of text.matchAll(BARE_AMOUNT)) {
        const [whole, cents] = m[1].split('.');
        if (whole.length < 4 && !cents)
            continue;
        const formatted = `$${groupThousands(whole)}${cents ? `.${cents.padEnd(2, '0')}` : ''}`;
        push(m.index, m.index + m[0].length, formatted, 'currency');
    }
    if (tightenUnits) {
        for (const m of text.matchAll(SPELLED_UNIT)) {
            const abbrev = SPELLED_UNITS[m[2].toLowerCase()];
            if (!abbrev)
                continue;
            push(m.index, m.index + m[0].length, `${m[1]}${abbrev}`, 'measure', 'certain', 'the guide abbreviates the unit and closes it up against the value');
        }
        for (const m of text.matchAll(SPACED_UNIT)) {
            push(m.index, m.index + m[0].length, `${m[1]}${canonicalUnit(m[2])}`, 'measure', 'certain', 'the guide closes the gap between a value and its unit');
        }
    }
    for (const m of text.matchAll(TIGHT_UNIT)) {
        const canonical = canonicalUnit(m[2]);
        if (canonical === m[2])
            continue;
        push(m.index, m.index + m[0].length, `${m[1]}${canonical}`, 'measure');
    }
    return hits.sort((a, b) => a.start - b.start);
}
//# sourceMappingURL=measures.js.map